import React from 'react';
import { Form, ListGroup } from '@/components/ui';
import './ReportSidebar.css';

interface ReportSidebarProps {
  activeSection: string;
  setActiveSection: (section: string) => void;
  canCreateD2: boolean;
  canCreateD3: boolean;
  canCreateWildlife?: boolean;
  canCreateRockfall?: boolean;
  canCreateSigns?: boolean;
  canCreateCulverts?: boolean;
  canCreateCattleguard?: boolean;
  canCreateEquipmentUsage?: boolean;
  canCreateMaterialUsage?: boolean;
  canCreateOverdueTasks?: boolean;
  canCreateLabourSubmittedTime?: boolean;
  canCreateQBS?: boolean;
  canCreateWeeklyPayroll?: boolean;
  canCreatePayrollVariance?: boolean;
  canViewFinanceMonthEnd?: boolean;
  canViewReports?: boolean;
  isMaterialControllerOnly?: boolean; // True if user only has material_usage_create permission (not report:view)
}

const ReportSidebar: React.FC<ReportSidebarProps> = ({
  activeSection,
  setActiveSection,
  canCreateD2,
  canCreateD3,
  canCreateWildlife = true,
  canCreateRockfall = true,
  canCreateSigns = true,
  canCreateCulverts = true,
  canCreateCattleguard = true,
  canCreateEquipmentUsage = true,
  canCreateMaterialUsage = true,
  canCreateOverdueTasks = true,
  canCreateLabourSubmittedTime = false,
  canCreateQBS = false,
  canCreateWeeklyPayroll = false,
  canCreatePayrollVariance = false,
  canViewFinanceMonthEnd = false,
  canViewReports = true,
  isMaterialControllerOnly = false
}) => {
  const mottOpen = activeSection.startsWith('mott');
  const financeOpen = activeSection.startsWith('finance');
  const payrollOpen = activeSection.startsWith('payroll');
  const operationsOpen = activeSection.startsWith('operations');

  // If Material Controller only, show only Material Usage Report
  if (isMaterialControllerOnly) {
    return (
      // h-full instead of minHeight: 100vh - the reporting shell is already a bounded flex pane
      // (see ReportingView), so 100vh double-counted the navbar/page chrome and pushed the report
      // content off screen on a phone ("Reports not visible at all in mobile view").
      <div className="tw-bg-surface-alt tw-border-r tw-border-line report-sidebar tw-h-full tw-overflow-y-auto">
        <div className="tw-p-2">
          <h5 className="tw-mb-3">Reports</h5>
          <ListGroup variant="flush">
            {canCreateMaterialUsage && (
              <ListGroup.Item
                action
                active={activeSection === 'operations-material-usage'}
                onClick={() => setActiveSection('operations-material-usage')}
                className="tw-border-0 tw-px-2 tw-py-2"
                style={{ 
                  borderRadius: '6px',
                  marginBottom: '2px'
                }}
              >
                <span className={activeSection === 'operations-material-usage' ? 'tw-font-bold' : ''}>
                  Material Usage
                </span>
              </ListGroup.Item>
            )}
          </ListGroup>
        </div>
      </div>
    );
  }

  return (
    // h-full + its own overflow-y-auto instead of minHeight: 100vh: the reporting shell already
    // bounds this pane's height (see ReportingView), so 100vh made the sidebar taller than the
    // visible area and pushed the actual report off screen - "Reports not visible at all in
    // mobile view". flex-col so the desktop nav tree (below) can scroll independently of the
    // mobile picker above it.
    <div className="tw-bg-surface-alt tw-border-r tw-border-line report-sidebar tw-h-full tw-flex tw-flex-col tw-overflow-hidden">
      {/* Below md there's no room for a full-height list of every report next to the report
          itself, so collapse the nav tree to a single <select> here and hide the tree (below). */}
      <div className="tw-flex-shrink-0 tw-border-b tw-border-line tw-p-2 md:tw-hidden">
        <Form.Group controlId="report-sidebar-picker" className="tw-mb-0">
          <Form.Label className="tw-text-sm tw-mb-1">Report</Form.Label>
          <Form.Select
            size="sm"
            value={activeSection}
            onChange={(e) => setActiveSection(e.target.value)}
          >
            <option value="">Select a report…</option>
            <option value="export-history">Export History</option>
            <optgroup label="MOTT Reports">
              {canCreateD2 && <option value="mott-d2">D2</option>}
              {canCreateD3 && <option value="mott-d3">D3</option>}
              {canCreateSigns && <option value="mott-chris-signs">CHRIS - Signs</option>}
              {canCreateCulverts && <option value="mott-chris-culverts">CHRIS - Culverts</option>}
              {canCreateCattleguard && <option value="mott-chris-cattleguards">CHRIS - Cattleguards</option>}
              <option value="mott-wildlife">Wildlife</option>
              {canCreateRockfall && <option value="mott-rockfall">Rockfall</option>}
            </optgroup>
            <optgroup label="Finance Reports">
              {canViewFinanceMonthEnd && <option value="finance-monthend">Month-End Report</option>}
              {canCreateEquipmentUsage && <option value="finance-equipment-usage">Equipment Usage</option>}
              <option value="finance-preliminary-job-cost">Preliminary Job Cost</option>
              {canCreateQBS && <option value="finance-quantified-costing">Quantified Costing</option>}
              {canCreateQBS && <option value="finance-quantified-value">Quantified Value</option>}
            </optgroup>
            {(canCreateWeeklyPayroll || canCreatePayrollVariance) && (
              <optgroup label="Payroll Reports">
                {canCreateWeeklyPayroll && <option value="payroll-weekly">Payroll Report</option>}
                {canCreateWeeklyPayroll && <option value="payroll-weekly-overtime">Overtime Report</option>}
                {canCreatePayrollVariance && <option value="payroll-variance">Payroll Variance Report</option>}
              </optgroup>
            )}
            <optgroup label="Operations Reports">
              {canCreateOverdueTasks && <option value="operations-overdue-tasks">Overdue Tasks</option>}
              {canCreateMaterialUsage && <option value="operations-material-usage">Material Usage</option>}
              {canCreateLabourSubmittedTime && canViewReports && (
                <option value="operations-labour">Submitted time</option>
              )}
              {canCreateQBS && <option value="operations-qbs-summary">QBS Summary</option>}
              {canCreateQBS && <option value="operations-qbs-project-summary">Project Summary</option>}
            </optgroup>
          </Form.Select>
        </Form.Group>
      </div>
      <div className="tw-hidden tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-p-2 md:tw-block">
        <h5 className="tw-mb-3">Reports</h5>
        <ListGroup variant="flush">
          <ListGroup.Item
            action
            active={activeSection === 'export-history'}
            onClick={() => setActiveSection('export-history')}
            className="tw-border-0 tw-px-2 tw-py-2"
            style={{ 
              borderRadius: '6px',
              marginBottom: '2px'
            }}
          >
            <i className="bi bi-clock-history tw-mr-2"></i>
            <span className={activeSection === 'export-history' ? 'tw-font-bold' : ''}>
              Export History
            </span>
          </ListGroup.Item>

          {/* MOTT Reports parent */}
          <ListGroup.Item
            action
            active={mottOpen}
            onClick={() => setActiveSection(mottOpen ? '' : 'mott')}
            className="tw-border-0 tw-px-2 tw-py-2 tw-flex tw-items-center tw-justify-between"
            style={{ 
              cursor: 'pointer',
              borderRadius: '6px',
              marginBottom: '2px'
            }}
          >
            <span>
              <i className="bi bi-file-earmark-text tw-mr-2"></i>
              <span className={mottOpen ? 'tw-font-bold' : ''}>
                MOTT Reports
              </span>
            </span>
            <i className={`bi tw-ml-2 ${mottOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
          </ListGroup.Item>
          {/* MOTT Reports submenu */}
          {mottOpen && (
            <div className="tw-pl-3">
              <ListGroup variant="flush">
                {canCreateD2 && (
                  <ListGroup.Item
                    action
                    active={activeSection === 'mott-d2'}
                    onClick={() => setActiveSection('mott-d2')}
                    className="tw-border-0 tw-px-2 tw-py-2"
                    style={{ 
                      borderRadius: '6px',
                      marginBottom: '2px'
                    }}
                  >
                    <span className={activeSection === 'mott-d2' ? 'tw-font-bold' : ''}>
                      D2
                    </span>
                  </ListGroup.Item>
                )}
                {canCreateD3 && (
                  <ListGroup.Item
                    action
                    active={activeSection === 'mott-d3'}
                    onClick={() => setActiveSection('mott-d3')}
                    className="tw-border-0 tw-px-2 tw-py-2"
                    style={{ 
                      borderRadius: '6px',
                      marginBottom: '2px'
                    }}
                  >
                    <span className={activeSection === 'mott-d3' ? 'tw-font-bold' : ''}>
                      D3
                    </span>
                  </ListGroup.Item>
                )}
                {canCreateSigns && (
                  <ListGroup.Item
                    action
                    active={activeSection === 'mott-chris-signs'}
                    onClick={() => setActiveSection('mott-chris-signs')}
                    className="tw-border-0 tw-px-2 tw-py-2"
                    style={{ 
                      borderRadius: '6px',
                      marginBottom: '2px'
                    }}
                  >
                    <span className={activeSection === 'mott-chris-signs' ? 'tw-font-bold' : ''}>
                      CHRIS - Signs
                    </span>
                  </ListGroup.Item>
                )}
                {canCreateCulverts && (
                  <ListGroup.Item
                    action
                    active={activeSection === 'mott-chris-culverts'}
                    onClick={() => setActiveSection('mott-chris-culverts')}
                    className="tw-border-0 tw-px-2 tw-py-2"
                    style={{ 
                      borderRadius: '6px',
                      marginBottom: '2px'
                    }}
                  >
                    <span className={activeSection === 'mott-chris-culverts' ? 'tw-font-bold' : ''}>
                      CHRIS - Culverts
                    </span>
                  </ListGroup.Item>
                )}
                {canCreateCattleguard && (
                  <ListGroup.Item
                    action
                    active={activeSection === 'mott-chris-cattleguards'}
                    onClick={() => setActiveSection('mott-chris-cattleguards')}
                    className="tw-border-0 tw-px-2 tw-py-2"
                    style={{ 
                      borderRadius: '6px',
                      marginBottom: '2px'
                    }}
                  >
                    <span className={activeSection === 'mott-chris-cattleguards' ? 'tw-font-bold' : ''}>
                      CHRIS - Cattleguards
                    </span>
                  </ListGroup.Item>
                )}
                <ListGroup.Item
                  action
                  active={activeSection === 'mott-wildlife'}
                  onClick={() => setActiveSection('mott-wildlife')}
                  className="tw-border-0 tw-px-2 tw-py-2"
                  style={{ 
                    borderRadius: '6px',
                    marginBottom: '2px'
                  }}
                >
                  <span className={activeSection === 'mott-wildlife' ? 'tw-font-bold' : ''}>
                    Wildlife
                  </span>
                </ListGroup.Item>
                {canCreateRockfall && (
                  <ListGroup.Item
                    action
                    active={activeSection === 'mott-rockfall'}
                    onClick={() => setActiveSection('mott-rockfall')}
                    className="tw-border-0 tw-px-2 tw-py-2"
                    style={{ 
                      borderRadius: '6px',
                      marginBottom: '2px'
                    }}
                  >
                    <span className={activeSection === 'mott-rockfall' ? 'tw-font-bold' : ''}>
                      Rockfall
                    </span>
                  </ListGroup.Item>
                )}
              </ListGroup>
            </div>
          )}

          {/* Finance Reports parent */}
          <ListGroup.Item
            action
            active={financeOpen}
            onClick={() => setActiveSection(financeOpen ? '' : 'finance')}
            className="tw-border-0 tw-px-2 tw-py-2 tw-flex tw-items-center tw-justify-between"
            style={{ 
              cursor: 'pointer',
              borderRadius: '6px',
              marginBottom: '2px'
            }}
          >
            <span>
              <i className="bi bi-currency-dollar tw-mr-2"></i>
              <span className={financeOpen ? 'tw-font-bold' : ''}>
                Finance Reports
              </span>
            </span>
            <i className={`bi tw-ml-2 ${financeOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
          </ListGroup.Item>
          {/* Finance Reports submenu */}
          {financeOpen && (
            <div className="tw-pl-3">
              <ListGroup variant="flush">
                {canViewFinanceMonthEnd && (
                  <ListGroup.Item
                    action
                    active={activeSection === 'finance-monthend'}
                    onClick={() => setActiveSection('finance-monthend')}
                    className="tw-border-0 tw-px-2 tw-py-2"
                    style={{
                      borderRadius: '6px',
                      marginBottom: '2px'
                    }}
                  >
                    <span className={activeSection === 'finance-monthend' ? 'tw-font-bold' : ''}>
                      Month-End Report
                    </span>
                  </ListGroup.Item>
                )}
                {canCreateEquipmentUsage && (
                  <ListGroup.Item
                    action
                    active={activeSection === 'finance-equipment-usage'}
                    onClick={() => setActiveSection('finance-equipment-usage')}
                    className="tw-border-0 tw-px-2 tw-py-2"
                    style={{ 
                      borderRadius: '6px',
                      marginBottom: '2px'
                    }}
                  >
                    <span className={activeSection === 'finance-equipment-usage' ? 'tw-font-bold' : ''}>
                      Equipment Usage
                    </span>
                  </ListGroup.Item>
                )}
                <ListGroup.Item
                  action
                  active={activeSection === 'finance-preliminary-job-cost'}
                  onClick={() => setActiveSection('finance-preliminary-job-cost')}
                  className="tw-border-0 tw-px-2 tw-py-2"
                  style={{ 
                    borderRadius: '6px',
                    marginBottom: '2px'
                  }}
                >
                  <span className={activeSection === 'finance-preliminary-job-cost' ? 'tw-font-bold' : ''}>
                    Preliminary Job Cost
                  </span>
                </ListGroup.Item>
                {canCreateQBS && (
                  <ListGroup.Item
                    action
                    active={activeSection === 'finance-quantified-costing'}
                    onClick={() => setActiveSection('finance-quantified-costing')}
                    className="tw-border-0 tw-px-2 tw-py-2"
                    style={{ 
                      borderRadius: '6px',
                      marginBottom: '2px'
                    }}
                  >
                    <span className={activeSection === 'finance-quantified-costing' ? 'tw-font-bold' : ''}>
                      Quantified Costing
                    </span>
                  </ListGroup.Item>
                )}
                {canCreateQBS && (
                  <ListGroup.Item
                    action
                    active={activeSection === 'finance-quantified-value'}
                    onClick={() => setActiveSection('finance-quantified-value')}
                    className="tw-border-0 tw-px-2 tw-py-2"
                    style={{ 
                      borderRadius: '6px',
                      marginBottom: '2px'
                    }}
                  >
                    <span className={activeSection === 'finance-quantified-value' ? 'tw-font-bold' : ''}>
                      Quantified Value
                    </span>
                  </ListGroup.Item>
                )}
              </ListGroup>
            </div>
          )}

          {/* Payroll Reports parent */}
          {(canCreateWeeklyPayroll || canCreatePayrollVariance) && (
            <>
              <ListGroup.Item
                action
                active={payrollOpen}
                onClick={() => setActiveSection(payrollOpen ? '' : 'payroll')}
                className="tw-border-0 tw-px-2 tw-py-2 tw-flex tw-items-center tw-justify-between"
                style={{
                  cursor: 'pointer',
                  borderRadius: '6px',
                  marginBottom: '2px'
                }}
              >
                <span>
                  <i className="bi bi-cash-coin tw-mr-2"></i>
                  <span className={payrollOpen ? 'tw-font-bold' : ''}>Payroll Reports</span>
                </span>
                <i className={`bi tw-ml-2 ${payrollOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
              </ListGroup.Item>
              {payrollOpen && (
                <div className="tw-pl-3">
                  <ListGroup variant="flush">
                    {canCreateWeeklyPayroll && (
                      <>
                        <ListGroup.Item
                          action
                          active={activeSection === 'payroll-weekly'}
                          onClick={() => setActiveSection('payroll-weekly')}
                          className="tw-border-0 tw-px-2 tw-py-2"
                          style={{
                            borderRadius: '6px',
                            marginBottom: '2px'
                          }}
                        >
                          <span className={activeSection === 'payroll-weekly' ? 'tw-font-bold' : ''}>
                            Payroll Report
                          </span>
                        </ListGroup.Item>
                        <ListGroup.Item
                          action
                          active={activeSection === 'payroll-weekly-overtime'}
                          onClick={() => setActiveSection('payroll-weekly-overtime')}
                          className="tw-border-0 tw-px-2 tw-py-2"
                          style={{
                            borderRadius: '6px',
                            marginBottom: '2px'
                          }}
                        >
                          <span className={activeSection === 'payroll-weekly-overtime' ? 'tw-font-bold' : ''}>
                            Overtime Report
                          </span>
                        </ListGroup.Item>
                      </>
                    )}
                    {canCreatePayrollVariance && (
                      <ListGroup.Item
                        action
                        active={activeSection === 'payroll-variance'}
                        onClick={() => setActiveSection('payroll-variance')}
                        className="tw-border-0 tw-px-2 tw-py-2"
                        style={{
                          borderRadius: '6px',
                          marginBottom: '2px'
                        }}
                      >
                        <span className={activeSection === 'payroll-variance' ? 'tw-font-bold' : ''}>
                          Payroll Variance Report
                        </span>
                      </ListGroup.Item>
                    )}
                  </ListGroup>
                </div>
              )}
            </>
          )}

          {/* Operations Reports parent */}
          <ListGroup.Item
            action
            active={operationsOpen}
            onClick={() => setActiveSection(operationsOpen ? '' : 'operations')}
            className="tw-border-0 tw-px-2 tw-py-2 tw-flex tw-items-center tw-justify-between"
            style={{ 
              cursor: 'pointer',
              borderRadius: '6px',
              marginBottom: '2px'
            }}
          >
            <span>
              <i className="bi bi-truck tw-mr-2"></i>
              <span className={operationsOpen ? 'tw-font-bold' : ''}>
                Operations Reports
              </span>
            </span>
            <i className={`bi tw-ml-2 ${operationsOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
          </ListGroup.Item>
          {/* Operations Reports submenu */}
          {operationsOpen && (
            <div className="tw-pl-3">
              <ListGroup variant="flush">
                {canCreateOverdueTasks && (
                  <ListGroup.Item
                    action
                    active={activeSection === 'operations-overdue-tasks'}
                    onClick={() => setActiveSection('operations-overdue-tasks')}
                    className="tw-border-0 tw-px-2 tw-py-2"
                    style={{ 
                      borderRadius: '6px',
                      marginBottom: '2px'
                    }}
                  >
                    <span className={activeSection === 'operations-overdue-tasks' ? 'tw-font-bold' : ''}>
                      Overdue Tasks
                    </span>
                  </ListGroup.Item>
                )}
                {canCreateMaterialUsage && (
                  <ListGroup.Item
                    action
                    active={activeSection === 'operations-material-usage'}
                    onClick={() => setActiveSection('operations-material-usage')}
                    className="tw-border-0 tw-px-2 tw-py-2"
                    style={{ 
                      borderRadius: '6px',
                      marginBottom: '2px'
                    }}
                  >
                    <span className={activeSection === 'operations-material-usage' ? 'tw-font-bold' : ''}>
                      Material Usage
                    </span>
                  </ListGroup.Item>
                )}
                {canCreateLabourSubmittedTime && canViewReports && (
                  <ListGroup.Item
                    action
                    active={activeSection === 'operations-labour'}
                    onClick={() => setActiveSection('operations-labour')}
                    className="tw-border-0 tw-px-2 tw-py-2"
                    style={{
                      borderRadius: '6px',
                      marginBottom: '2px'
                    }}
                  >
                    <span className={activeSection === 'operations-labour' ? 'tw-font-bold' : ''}>
                      Submitted time
                    </span>
                  </ListGroup.Item>
                )}
                {canCreateQBS && (
                  <>
                    <ListGroup.Item
                      action
                      active={activeSection === 'operations-qbs-summary'}
                      onClick={() => setActiveSection('operations-qbs-summary')}
                      className="tw-border-0 tw-px-2 tw-py-2"
                      style={{ 
                        borderRadius: '6px',
                        marginBottom: '2px'
                      }}
                    >
                      <span className={activeSection === 'operations-qbs-summary' ? 'tw-font-bold' : ''}>
                        QBS Summary
                      </span>
                    </ListGroup.Item>
                    <ListGroup.Item
                      action
                      active={activeSection === 'operations-qbs-project-summary'}
                      onClick={() => setActiveSection('operations-qbs-project-summary')}
                      className="tw-border-0 tw-px-2 tw-py-2"
                      style={{ 
                        borderRadius: '6px',
                        marginBottom: '2px'
                      }}
                    >
                      <span className={activeSection === 'operations-qbs-project-summary' ? 'tw-font-bold' : ''}>
                        Project Summary
                      </span>
                    </ListGroup.Item>
                  </>
                )}
              </ListGroup>
            </div>
          )}

        </ListGroup>
      </div>
    </div>
  );
};

export default ReportSidebar; 